import type { SidebarLayoutPref } from '@staiter/shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// Эфемерные preferences: sidebar / exports tray / last filters. persist middleware → localStorage.
// Sidebar layout также синкается с сервером через /me/preferences (sidebar_layout key) —
// localStorage остаётся offline-кэшем, server = source of truth (persists per-user across devices).
// Именованные Saved Views — server-side (см. lib/savedViews.ts).
type CustomSidebarSection = { id: string; title: string };

type UiState = {
  sidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (v: boolean) => void;
  /**
   * chat-interactive-actions F17 [Х9] — ВРЕМЕННОЕ сворачивание меню (на время
   * открытой чат-панели). Не персистится: `null` — «своего мнения нет, читай
   * выбор пользователя».
   *
   * Почему не писать прямо в `sidebarCollapsed`: он персистится в localStorage
   * и уезжает в настройки пользователя. Чат, свернувший меню на минуту,
   * оставил бы его свёрнутым НАВСЕГДА — либо затёр бы ручной тоггл, сделанный
   * при открытой панели.
   */
  sidebarCollapsedOverride: boolean | null;
  setSidebarCollapsedOverride: (v: boolean | null) => void;
  // Sidebar sections — collapsible + re-orderable per user 2026-06-08.
  sidebarSectionsCollapsed: Record<string, boolean>;
  toggleSidebarSection: (id: string) => void;
  sidebarSectionOrder: string[];
  setSidebarSectionOrder: (order: string[]) => void;
  // 2026-06-08 phase 2: section title overrides + custom sections + item-level DnD.
  sidebarSectionTitles: Record<string, string>;
  setSidebarSectionTitle: (id: string, title: string) => void;
  /** Custom (user-created) sections — id starts с `custom_`. */
  sidebarCustomSections: CustomSidebarSection[];
  addSidebarCustomSection: (title: string) => string; // returns generated id
  removeSidebarCustomSection: (id: string) => void;
  /** Per-item section override: route key (item.to) → sectionId. */
  sidebarItemSectionOverrides: Record<string, string>;
  setSidebarItemSection: (itemKey: string, sectionId: string) => void;
  /** Per-section item ordering: sectionId → array of item.to in order. */
  sidebarItemOrder: Record<string, string[]>;
  setSidebarItemOrder: (sectionId: string, order: string[]) => void;
  resetSidebarPrefs: () => void;
  /** Server hydration: rehydrates sidebar slice from server preferences (called after login). */
  hydrateSidebarFromServer: (layout: SidebarLayoutPref) => void;
  exportsTrayOpen: boolean;
  setExportsTrayOpen: (v: boolean) => void;
  // last-filters per route key → object JSON
  lastFilters: Record<string, Record<string, string | undefined>>;
  setLastFilters: (key: string, filters: Record<string, string | undefined>) => void;
};

const DEFAULT_SECTION_ORDER = [
  'projects',
  'agent_review',
  'workspaces',
  'inventory_ops',
  'finance',
  'reporting',
  'admin',
];

/**
 * go-live 2026-07-03 — секции, свёрнутые по умолчанию (до первого клика пользователя).
 * Свёрнутость читается как `saved ?? default`: нет записи → дефолт; первый toggle
 * пишет явное значение, и дальше решение пользователя персистится (localStorage +
 * /me/preferences). «Производство» (inventory_ops) при первом входе свёрнуто.
 */
export const DEFAULT_SECTIONS_COLLAPSED: Record<string, boolean> = {
  inventory_ops: true,
};

/** Свёрнута ли секция с учётом дефолтов (для render-слоя). */
export function isSectionCollapsed(saved: Record<string, boolean>, id: string): boolean {
  return saved[id] ?? DEFAULT_SECTIONS_COLLAPSED[id] ?? false;
}

export const useUiStore = create<UiState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),
      sidebarCollapsedOverride: null,
      setSidebarCollapsedOverride: (v) => set({ sidebarCollapsedOverride: v }),
      sidebarSectionsCollapsed: {},
      toggleSidebarSection: (id) =>
        set((s) => ({
          sidebarSectionsCollapsed: {
            ...s.sidebarSectionsCollapsed,
            [id]: !isSectionCollapsed(s.sidebarSectionsCollapsed, id),
          },
        })),
      sidebarSectionOrder: DEFAULT_SECTION_ORDER,
      setSidebarSectionOrder: (order) => set({ sidebarSectionOrder: order }),
      sidebarSectionTitles: {},
      setSidebarSectionTitle: (id, title) =>
        set((s) => ({
          sidebarSectionTitles: { ...s.sidebarSectionTitles, [id]: title },
        })),
      sidebarCustomSections: [],
      addSidebarCustomSection: (title) => {
        const id = `custom_${Math.random().toString(36).slice(2, 10)}`;
        set((s) => ({
          sidebarCustomSections: [...s.sidebarCustomSections, { id, title }],
          sidebarSectionOrder: [...s.sidebarSectionOrder, id],
        }));
        return id;
      },
      removeSidebarCustomSection: (id) =>
        set((s) => ({
          sidebarCustomSections: s.sidebarCustomSections.filter((sec) => sec.id !== id),
          sidebarSectionOrder: s.sidebarSectionOrder.filter((x) => x !== id),
          // Сбросить items которые были в custom section — они вернутся в default section.
          sidebarItemSectionOverrides: Object.fromEntries(
            Object.entries(s.sidebarItemSectionOverrides).filter(([, v]) => v !== id),
          ),
          sidebarItemOrder: Object.fromEntries(
            Object.entries(s.sidebarItemOrder).filter(([k]) => k !== id),
          ),
        })),
      sidebarItemSectionOverrides: {},
      setSidebarItemSection: (itemKey, sectionId) =>
        set((s) => ({
          sidebarItemSectionOverrides: { ...s.sidebarItemSectionOverrides, [itemKey]: sectionId },
        })),
      sidebarItemOrder: {},
      setSidebarItemOrder: (sectionId, order) =>
        set((s) => ({
          sidebarItemOrder: { ...s.sidebarItemOrder, [sectionId]: order },
        })),
      resetSidebarPrefs: () =>
        set({
          sidebarSectionsCollapsed: {},
          sidebarSectionOrder: DEFAULT_SECTION_ORDER,
          sidebarSectionTitles: {},
          sidebarCustomSections: [],
          sidebarItemSectionOverrides: {},
          sidebarItemOrder: {},
        }),
      hydrateSidebarFromServer: (layout) =>
        set({
          sidebarSectionsCollapsed: layout.sectionsCollapsed,
          sidebarSectionOrder:
            layout.sectionOrder.length > 0 ? layout.sectionOrder : DEFAULT_SECTION_ORDER,
          sidebarSectionTitles: layout.sectionTitles,
          sidebarCustomSections: layout.customSections,
          sidebarItemSectionOverrides: layout.itemSectionOverrides,
          sidebarItemOrder: layout.itemOrder,
        }),
      exportsTrayOpen: false,
      setExportsTrayOpen: (v) => set({ exportsTrayOpen: v }),
      lastFilters: {},
      setLastFilters: (key, filters) =>
        set((s) => ({ lastFilters: { ...s.lastFilters, [key]: filters } })),
    }),
    {
      name: 'staiter.ui',
      storage: createJSONStorage(() => localStorage),
      /**
       * F17 — временный override НЕ сохраняется. Без этого списка персистится
       * весь стор целиком, и «свернули меню на время чата» пережило бы
       * перезагрузку как решение пользователя.
       */
      partialize: (state) => {
        const { sidebarCollapsedOverride: _omit, ...rest } = state;
        return rest as UiState;
      },
    },
  ),
);

/** Selector — выдёргивает только sidebar-slice для server push. */
export function selectSidebarLayoutPref(state: UiState): SidebarLayoutPref {
  return {
    sectionsCollapsed: state.sidebarSectionsCollapsed,
    sectionOrder: state.sidebarSectionOrder,
    sectionTitles: state.sidebarSectionTitles,
    customSections: state.sidebarCustomSections,
    itemSectionOverrides: state.sidebarItemSectionOverrides,
    itemOrder: state.sidebarItemOrder,
  };
}
